// Plate-loading calculator. Pure function — given a target total
// weight, a bar weight, and an inventory of plate denominations,
// return the per-side plate stack that approximates the target. Used
// by the workout logger's "show plates" affordance so the athlete
// reads the load-out without doing the arithmetic on the gym floor.
//
// Algorithm: greedy by descending plate weight, capped by available
// pairs (each plate is loaded as a pair, one per side, so the
// inventory is per-pair). Float arithmetic uses round-to-3-decimals
// at every step so the typical 2.5 + 1.25 + 0.625 fractional kg case
// doesn't accumulate IEEE drift.
//
// Half-bar mode (used for landmine OHP / one-handed work): when only
// one side of the bar is loaded, the formula becomes
// target = bar + halfBarOffset + sideLoad, where halfBarOffset is the
// bar's CG shift produced by lifting from one end. We don't model
// halfBarOffset yet — the client passes a separate `barWeight` for
// half-bar lifts (typical: half the regular bar's weight) and the
// calc proceeds the same. Half-bar is a profile-level UX flag, not
// a calculator-level one.
//
// Inventory shape: `{ kg: number; pairs: number }[]`. Each entry is
// one plate denomination plus how many pairs the gym has. The
// default JL inventory matches a typical home / commercial
// setup with metric plates; the editor can override per profile in
// a future phase.

export type PlateInventoryEntry = {
  kg: number;
  pairs: number;
};

export type PlatePairLoaded = {
  kg: number;
  pairs: number;
};

export type PlateCalculation =
  | {
      ok: true;
      // 'pairs' = standard symmetric loading on both bar ends (default).
      // 'single' = landmine / one-side loading: the plates load on a
      // single end, so `perSideKg` reads as the loaded-end total and
      // each `pairsLoaded.pairs` is a plate count (not a pair count).
      mode: "pairs" | "single";
      perSideKg: number;
      pairsLoaded: PlatePairLoaded[];
      // Tail = how much we couldn't represent with the inventory.
      // 0 means an exact match; positive means the target is heavier
      // than what fits; we still return the closest-from-below load
      // so the lift isn't aborted.
      remainderKg: number;
    }
  | { ok: false; reason: "below_bar" | "invalid_input" };

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

// JL default plate inventory — covers 1.25 → 25 kg plates with a
// generous pair count. The greedy walk only consumes what it needs,
// so pair counts are a "max available" hint not a target.
export const DEFAULT_PLATE_INVENTORY: PlateInventoryEntry[] = [
  { kg: 25, pairs: 4 },
  { kg: 20, pairs: 4 },
  { kg: 15, pairs: 2 },
  { kg: 10, pairs: 4 },
  { kg: 5, pairs: 4 },
  { kg: 2.5, pairs: 4 },
  { kg: 1.25, pairs: 4 },
  { kg: 0.5, pairs: 4 },
];

// Standard Olympic barbell weight defaults. Used when the profile
// hasn't configured a per-exercise barbell weight. The 7 kg curl-bar
// and 11 kg trap-bar live in the per-exercise barbell map — added
// as a future phase.
export const DEFAULT_BARBELL_KG = 20;

// Default loadable increments used when the athlete hasn't configured a
// plate inventory (LG-15). Kg default mirrors StrengthLog's "nearest 2.5 kg";
// lb default is 5 lb (a pair of 2.5 lb plates) — the common finest step in a
// pound gym. These are the fallback for `roundLoadable`; a configured
// inventory refines the kg step via `smallestLoadableStepKg`. Kept here beside
// the inventory constant so "how big is one plate step" has one home.
export const DEFAULT_LOADABLE_STEP_KG = 2.5;
export const DEFAULT_LOADABLE_STEP_LB = 5;

// Finest total-load increment an inventory can express. Symmetric ("pairs")
// loading adds a plate to BOTH ends at once, so the smallest step is 2× the
// smallest plate; single-side ("single", landmine) adds one plate → 1×. Used
// by the %1RM rounding path so a computed target snaps to a weight the athlete
// can actually build (LG-15). Falls back to the kg default when the inventory
// is empty of usable plates.
export const smallestLoadableStepKg = (
  inventory: PlateInventoryEntry[] = DEFAULT_PLATE_INVENTORY,
  mode: "pairs" | "single" = "pairs",
): number => {
  const usable = inventory.filter((p) => p.kg > 0 && p.pairs > 0);
  if (usable.length === 0) return DEFAULT_LOADABLE_STEP_KG;
  const smallest = Math.min(...usable.map((p) => p.kg));
  return round3(mode === "pairs" ? smallest * 2 : smallest);
};

export const computePlates = (params: {
  targetKg: number;
  barKg?: number;
  inventory?: PlateInventoryEntry[];
  /** Loading shape. 'standard' (default) loads pairs symmetrically;
   *  'landmine' loads a single stack on the free end (the other end
   *  is anchored to the floor pivot). */
  barLoading?: "standard" | "landmine";
}): PlateCalculation => {
  const bar = params.barKg ?? DEFAULT_BARBELL_KG;
  const target = params.targetKg;
  const mode = params.barLoading === "landmine" ? "single" : "pairs";
  if (!Number.isFinite(target) || target < 0 || !Number.isFinite(bar) || bar < 0) {
    return { ok: false, reason: "invalid_input" };
  }
  if (target < bar) {
    return { ok: false, reason: "below_bar" };
  }
  const totalLoad = round3(target - bar);
  const inv = (params.inventory ?? DEFAULT_PLATE_INVENTORY)
    .filter((p) => p.kg > 0 && p.pairs > 0)
    .slice()
    .sort((a, b) => b.kg - a.kg);

  if (mode === "single") {
    // Landmine: a single plate stack on the loaded end. Each inventory
    // entry's `pairs` count contributes 2 plates (one pair = 2 plates),
    // so the available count per kg is `pairs × 2` for landmine work.
    let remaining = totalLoad;
    const platesLoaded: PlatePairLoaded[] = [];
    for (const entry of inv) {
      if (remaining <= 0) break;
      // ⚠️ DEFENSIVE AND CURRENTLY DEAD — the `+ 1e-6` cannot change this
      // result, and the same is true of its twin in the pairs loop below.
      // It is there to stop a quotient that lands a hair under an integer
      // (49.999999999999996) from flooring to 48 — but `round3` already
      // snapped the quotient to 0.001 BEFORE the epsilon is added, and
      // realistic IEEE-754 drift (~1e-16 relative) is orders of magnitude
      // below that snap radius. Verified by exhaustive sweep (kg
      // 0.001-3.000 × k 1-5000) and a 5M-round fuzz of the full loop with
      // and without it: zero divergences.
      //
      // Kept rather than deleted because it becomes load-bearing again the
      // moment anyone removes the `round3` around the division — which is
      // exactly the kind of "simplification" that looks safe. Concretely,
      // with 0.05 kg microplates and 0.15 kg remaining, the raw quotient is
      // 2.9999999999999996: without round3 the floor gives 2 plates instead
      // of 3, and only the epsilon saves it. Across 0.05-step plate weights
      // that case is common, not exotic. If you take round3 out, keep the
      // epsilon and add a test.
      const maxByRemainder = Math.floor(round3(remaining / entry.kg) + 1e-6);
      const platesAvailable = entry.pairs * 2;
      const platesToUse = Math.min(maxByRemainder, platesAvailable);
      if (platesToUse <= 0) continue;
      // `pairs` field reused as plate count for single-stack loading.
      platesLoaded.push({ kg: entry.kg, pairs: platesToUse });
      remaining = round3(remaining - platesToUse * entry.kg);
    }
    return {
      ok: true,
      mode: "single",
      perSideKg: round3(totalLoad - remaining),
      pairsLoaded: platesLoaded,
      remainderKg: round3(remaining),
    };
  }

  // Standard pairs loading. Each pair covers one plate per side, so
  // kg-per-side decreases by entry.kg per pair.
  const perSide = round3(totalLoad / 2);
  let perSideRemaining = perSide;
  const pairsLoaded: PlatePairLoaded[] = [];
  for (const entry of inv) {
    if (perSideRemaining <= 0) break;
    // Same defensive-and-dead epsilon as the single-stack loop above; the
    // reasoning and the "if you remove round3, keep this" note live there.
    const maxByRemainder = Math.floor(round3(perSideRemaining / entry.kg) + 1e-6);
    const pairsToUse = Math.min(maxByRemainder, entry.pairs);
    if (pairsToUse <= 0) continue;
    pairsLoaded.push({ kg: entry.kg, pairs: pairsToUse });
    perSideRemaining = round3(perSideRemaining - pairsToUse * entry.kg);
  }
  return {
    ok: true,
    mode: "pairs",
    perSideKg: round3(perSide - perSideRemaining),
    pairsLoaded,
    remainderKg: round3(perSideRemaining * 2),
  };
};
